import { Component, Input, ElementRef, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-html-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `<div #container></div>`,
  styles: [`:host { display: block; width: 100%; }`],
  encapsulation: ViewEncapsulation.ShadowDom
})
export class HtmlRendererComponent implements OnChanges {
  @Input() htmlContent: string = '';
  @Input() baseStyles: string = '';

  private uniqueId = 'html-renderer-' + Math.random().toString(36).substring(2, 9);

  constructor(private el: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['htmlContent'] || changes['baseStyles']) {
      this.renderContent();
    }
  }

  private renderContent(): void {
    if (!this.htmlContent) return;

    const shadowRoot = this.el.nativeElement.shadowRoot;
    const container = shadowRoot.querySelector('div');
    if (!container) return;

    container.innerHTML = '';

    // Base styles
    const style = document.createElement('style');
    style.textContent = this.baseStyles;
    container.appendChild(style);

    // Register helpers directly from Angular — no setup script needed
    (window as any)[this.uniqueId] = {
      shadowRoot,
      helpers: {
        getElement: (sel: string) => shadowRoot.querySelector(sel),
        getCanvas: (sel: string) => {
          const el = shadowRoot.querySelector(sel);
          if (!el) console.warn('[html-renderer] canvas not found:', sel);
          return el;
        },
        getContext: (sel: string, type = '2d') => {
          const canvas = shadowRoot.querySelector(sel) as HTMLCanvasElement | null;
          if (!canvas) { console.warn('[html-renderer] canvas not found:', sel); return null; }
          if ((window as any).Chart?.getChart?.(canvas)) {
            (window as any).Chart.getChart(canvas).destroy();
          }
          // Ensure canvas has non-zero dimensions so Chart.js renders correctly
          if (!canvas.width || canvas.width === 0) {
            canvas.width = canvas.parentElement?.clientWidth || 600;
          }
          return canvas.getContext(type);
        },
      },
    };

    // Parse source HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.htmlContent;

    const allScripts = Array.from(tempDiv.querySelectorAll('script'));
    const externalScripts = allScripts.filter(s => !!s.src);
    const inlineScripts = allScripts.filter(s => !s.src);

    // Append non-script HTML nodes
    Array.from(tempDiv.childNodes).forEach(node => {
      if (!(node instanceof HTMLScriptElement)) {
        container.appendChild(node.cloneNode(true));
      }
    });

    // Load all external scripts (e.g. Chart.js CDN) first, then run inline scripts
    // Deduplicate by src so re-renders don't re-append the same CDN script
    const loadedSrcs = new Set<string>(
      Array.from(document.querySelectorAll('script[src]')).map((s: any) => s.src)
    );
    const loadExternals = externalScripts.map(oldScript => {
      return new Promise<void>(resolve => {
        if (loadedSrcs.has(oldScript.src)) { resolve(); return; }
        const s = document.createElement('script');
        s.src = oldScript.src;
        s.onload = () => { loadedSrcs.add(s.src); resolve(); };
        s.onerror = () => {
          console.error('[html-renderer] failed to load:', oldScript.src);
          resolve(); // don't block inline scripts on failure
        };
        document.head.appendChild(s); // append to head so it's truly global and deduplicated
      });
    });

    Promise.all(loadExternals).then(() => {
      // Now run inline scripts with shadow DOM helpers in scope
      inlineScripts.forEach(oldScript => {
        const s = document.createElement('script');
        s.textContent = `
          (function() {
            try {
              var _inst = window['${this.uniqueId}'];
              if (!_inst) { console.error('[html-renderer] instance not found'); return; }
              var getElement = function(sel) { return _inst.helpers.getElement(sel); };
              var getCanvas  = function(sel) { return _inst.helpers.getCanvas(sel); };
              var getContext = function(sel, type) { return _inst.helpers.getContext(sel, type); };
              // Shadow document so AI-generated getElementById/querySelector works inside ShadowDOM
              var document = {
                getElementById: function(id) { return _inst.shadowRoot.querySelector('#' + id); },
                querySelector: function(sel) { return _inst.shadowRoot.querySelector(sel); },
                querySelectorAll: function(sel) { return _inst.shadowRoot.querySelectorAll(sel); },
                createElement: function(tag) { return window.document.createElement(tag); },
              };
              ${oldScript.textContent}
            } catch(e) {
              console.error('[html-renderer] script error:', e);
            }
          })();
        `;
        container.appendChild(s);
      });

      // Cleanup global reference after scripts have run
      setTimeout(() => {
        try { delete (window as any)[this.uniqueId]; } catch (_) {}
      }, 2000);
    });
  }
}
